class FlavorSlider extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.product-flavor-slider__track');
    this.progressBar = this.querySelector('.product-flavor-slider__progress');
    this.indicator = this.querySelector('.product-flavor-slider__progress-indicator');
    this.prevButton = this.querySelector('[name="previous"]');
    this.nextButton = this.querySelector('[name="next"]');
    this.isDragging = false;
    this.hasDragged = false;
    this.suppressClick = false;
    this.dragStartX = 0;
    this.dragScrollStart = 0;
    this.activePointerId = null;
  }

  connectedCallback() {
    if (!this.track || !this.indicator || !this.progressBar) return;

    this.items = this.querySelectorAll('.product-flavor-slider__item');
    this.onScroll = this.updateProgress.bind(this);
    this.onResize = this.updateProgress.bind(this);

    this.boundPointerDown = this.onPointerDown.bind(this);
    this.boundPointerMove = this.onPointerMove.bind(this);
    this.boundPointerUp = this.onPointerUp.bind(this);
    this.boundClickCapture = this.onClickCapture.bind(this);
    this.boundItemClick = this.onItemClick.bind(this);

    this.scrollActiveIntoView();

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);

    this.track.addEventListener('pointerdown', this.boundPointerDown);
    this.track.addEventListener('click', this.boundClickCapture, true);
    if (this.dataset.flavorClick === 'swap') {
      this.track.addEventListener('click', this.boundItemClick);
    }

    this.prevButton?.addEventListener('click', () => this.scrollByItem(-1));
    this.nextButton?.addEventListener('click', () => this.scrollByItem(1));
  }

  disconnectedCallback() {
    this.endDrag();
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.track?.removeEventListener('pointerdown', this.boundPointerDown);
    this.track?.removeEventListener('click', this.boundClickCapture, true);
    this.track?.removeEventListener('click', this.boundItemClick);
  }

  onItemClick(event) {
    if (this.suppressClick) return;

    const item = event.target.closest('.product-flavor-slider__item[data-flavor-action="swap"]');
    if (!item || item.classList.contains('product-flavor-slider__item--active')) return;

    event.preventDefault();
    const productInfo = this.closest('product-info');
    productInfo?.swapToProduct?.(item.dataset.productUrl);
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.activePointerId = event.pointerId;
    this.isDragging = false;
    this.hasDragged = false;
    this.dragStartX = event.clientX;
    this.dragScrollStart = this.track.scrollLeft;

    window.addEventListener('pointermove', this.boundPointerMove);
    window.addEventListener('pointerup', this.boundPointerUp);
    window.addEventListener('pointercancel', this.boundPointerUp);
  }

  onPointerMove(event) {
    if (this.activePointerId === null || event.pointerId !== this.activePointerId) return;

    const deltaX = event.clientX - this.dragStartX;

    if (!this.isDragging) {
      if (Math.abs(deltaX) < 10) return;

      this.isDragging = true;
      this.hasDragged = true;
      this.track.classList.add('is-dragging');
    }

    this.track.scrollLeft = this.dragScrollStart - deltaX;
    event.preventDefault();
  }

  onPointerUp(event) {
    if (this.activePointerId === null || event.pointerId !== this.activePointerId) return;

    if (this.hasDragged) {
      this.suppressClick = true;
      window.setTimeout(() => {
        this.suppressClick = false;
      }, 150);
    }

    this.endDrag();
  }

  endDrag() {
    this.isDragging = false;
    this.activePointerId = null;
    this.track?.classList.remove('is-dragging');

    window.removeEventListener('pointermove', this.boundPointerMove);
    window.removeEventListener('pointerup', this.boundPointerUp);
    window.removeEventListener('pointercancel', this.boundPointerUp);
  }

  onClickCapture(event) {
    if (!this.suppressClick) return;

    event.preventDefault();
    event.stopPropagation();
  }

  updateProgress() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const maxScroll = scrollWidth - clientWidth;
    const active = this.querySelector('.product-flavor-slider__item--active');

    if (active) {
      const progressRect = this.progressBar.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      const barWidth = progressRect.width;
      let left = activeRect.left - progressRect.left;
      let width = activeRect.width;

      // Keep indicator within the progress bar when the active item is partially off-screen
      if (left < 0) {
        width += left;
        left = 0;
      }
      if (left + width > barWidth) {
        width = barWidth - left;
      }
      width = Math.max(0, width);

      this.indicator.style.width = `${width}px`;
      this.indicator.style.left = `${left}px`;
    }

    if (maxScroll <= 0) {
      this.prevButton?.setAttribute('disabled', 'disabled');
      this.nextButton?.setAttribute('disabled', 'disabled');
      return;
    }

    if (scrollLeft <= 1) {
      this.prevButton?.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton?.removeAttribute('disabled');
    }

    if (scrollLeft >= maxScroll - 1) {
      this.nextButton?.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton?.removeAttribute('disabled');
    }
  }

  scrollByItem(direction) {
    const item = this.items[0];
    if (!item) return;

    const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 20;
    const step = item.offsetWidth + gap;

    this.track.scrollBy({
      left: direction * step,
      behavior: 'smooth',
    });
  }

  scrollActiveIntoView() {
    const active = this.querySelector('.product-flavor-slider__item--active');
    if (!active) return;

    const alignActive = () => {
      // Active flavor is rendered first in the track — start at the beginning
      this.track.scrollLeft = 0;
      this.updateProgress();
    };

    requestAnimationFrame(alignActive);

    this.track.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', alignActive, { once: true });
      }
    });
  }
}

customElements.define('flavor-slider', FlavorSlider);
